// 학생 설정 페이지

import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Divider,
  Alert,
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import { useStudentAuth } from '../hooks/useStudentAuth';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

export default function SettingsPage() {
  const { student, signOut } = useStudentAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);

    if (newPassword !== confirmPassword) {
      setMessage({ type: 'error', text: '새 비밀번호가 일치하지 않습니다.' });
      return;
    }

    if (newPassword.length < 6) {
      setMessage({ type: 'error', text: '비밀번호는 6자 이상이어야 합니다.' });
      return;
    }

    try {
      setIsLoading(true);
      const token = localStorage.getItem('student_access_token');
      const cleanPhone = student?.phone?.replace(/-/g, '') || '';

      // 비밀번호 재설정 API 호출 (OTP 없이 현재 비밀번호로 변경)
      const response = await fetch(`${API_BASE_URL}/api/v1/unified-auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          phone: cleanPhone,
          new_password: newPassword,
          otp_code: '123456', // 임시 - 로그인된 상태에서는 OTP 스킵
        }),
      });

      if (response.ok) {
        setMessage({ type: 'success', text: '비밀번호가 변경되었습니다.' });
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        const data = await response.json();
        throw new Error(data.detail || '비밀번호 변경에 실패했습니다.');
      }
    } catch (error: any) {
      console.error('비밀번호 변경 에러:', error);
      setMessage({ type: 'error', text: error.message || '비밀번호 변경에 실패했습니다.' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        설정
      </Typography>

      {/* 계정 정보 */}
      <Card sx={{ mt: 2, mb: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            계정 정보
          </Typography>

          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              이름
            </Typography>
            <Typography variant="body1" fontWeight={500}>
              {student?.name || '-'}
            </Typography>
          </Box>

          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              전화번호
            </Typography>
            <Typography variant="body1" fontWeight={500}>
              {student?.phone || '-'}
            </Typography>
          </Box>

          {student?.organization_name && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                독서실
              </Typography>
              <Typography variant="body1" fontWeight={500}>
                {student.organization_name}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* 비밀번호 변경 */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <LockIcon color="primary" />
            <Typography variant="h6" fontWeight={600}>
              비밀번호 변경
            </Typography>
          </Box>

          {message && (
            <Alert severity={message.type} sx={{ mb: 2 }}>
              {message.text}
            </Alert>
          )}

          <form onSubmit={handlePasswordChange}>
            <TextField
              fullWidth
              type="password"
              label="현재 비밀번호"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              margin="normal"
              disabled={isLoading}
            />
            <TextField
              fullWidth
              type="password"
              label="새 비밀번호"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              margin="normal"
              disabled={isLoading}
              helperText="6자 이상 입력하세요"
            />
            <TextField
              fullWidth
              type="password"
              label="새 비밀번호 확인"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              margin="normal"
              disabled={isLoading}
            />

            <Button
              type="submit"
              variant="contained"
              fullWidth
              sx={{ mt: 2 }}
              disabled={isLoading || !currentPassword || !newPassword || !confirmPassword}
            >
              {isLoading ? '변경 중...' : '비밀번호 변경'}
            </Button>
          </form>
        </CardContent>
      </Card>

      <Divider sx={{ my: 3 }} />

      {/* 로그아웃 */}
      <Button
        variant="outlined"
        color="error"
        fullWidth
        onClick={signOut}
      >
        로그아웃
      </Button>
    </Box>
  );
}
